import {
    allowedOptionCodes,
    allowedOwnOptionValues,
    findHiddenInputIds,
    hasVisibilityCondition,
} from "@patient-watch/forms/pageLogic";
import type { InputT } from "@patient-watch/types";
import { useMemo } from "react";
import { type Control, useWatch } from "react-hook-form";

import { useEntryValuesStore } from "../../inputs/hooks/useEntryValuesStore";
import { buildAnswerMapFromEntryValues } from "./useFormBranchingNavigation";

export interface InputVisibility {
    /** Inputs hidden by show_when, or by options_from when no listed checkbox is ticked. */
    hidden: ReadonlySet<string>;
    /** For each options_from input, the choice codes its ticked checkboxes or picked choices allow. */
    allowedCodes: ReadonlyMap<string, number[]>;
    /** For each options_from input with `own: true`, the respondent's own options (ids) picked earlier. */
    allowedOwn: ReadonlyMap<string, string[]>;
}

const NONE: InputVisibility = { hidden: new Set(), allowedCodes: new Map(), allowedOwn: new Map() };

/**
 * Which inputs on this page are hidden, and which choices options_from inputs offer, worked out live from
 * the page's current values and the answers given on earlier pages. Pages without such inputs never
 * re-render on typing.
 */
export function useInputVisibility(control: Control<Record<string, unknown>>, inputs: InputT[]): InputVisibility {
    const conditional = useMemo(() => inputs.some((input) => hasVisibilityCondition(input)), [inputs]);
    const values = useWatch({ control, disabled: !conditional });
    const entryValues = useEntryValuesStore((state) => state.entry_values);

    return useMemo(() => {
        if (!conditional) return NONE;
        // The form can still hold a value for a question on an earlier page (e.g. an empty multiple-choice list), so
        // only this page's questions take their answer from the form; earlier ones come from the saved answers.
        const pageIds = new Set(inputs.map((input) => input.input_id));
        const pageValues = Object.fromEntries(
            Object.entries((values ?? {}) as Record<string, never>).filter(([id]) => pageIds.has(id))
        );
        const answers = { ...buildAnswerMapFromEntryValues(entryValues), ...pageValues };
        const allowedCodes = new Map<string, number[]>();
        const allowedOwn = new Map<string, string[]>();
        for (const input of inputs) {
            const codes = allowedOptionCodes(input, answers);
            if (codes) allowedCodes.set(input.input_id, codes);
            const own = allowedOwnOptionValues(input, answers);
            if (own) allowedOwn.set(input.input_id, own);
        }
        return { hidden: new Set(findHiddenInputIds(inputs, answers)), allowedCodes, allowedOwn };
    }, [conditional, entryValues, inputs, values]);
}
